// Non-React fire-and-forget analytics for the navigational-features domain.
// Mirrors the canonical guarded idiom (visual-query-editor/edit/commitEdit.ts).
//
// PRD: Ralph/NavigationalFeatures/PRD.md — analytics instrumentation (NAV-6).

import type { CodeActionId, IdentifierKind, RenameKind } from '../types';

// ─── Auxiliary union types ───────────────────────────────────────────────────

/** All `via` values of a Go to Definition navigation (`definitionNavigationOpener.ts`). */
export type GotoVia =
  | 'object-explorer'
  | 'column-target'
  | 'routine-target'
  | 'routine-picker'
  | 'alias-decl'
  | 'cte-body';

/** Whether the user arrived via keybinding (F12 / Shift+F12 / F2) or via
 *  an Object-Explorer context-menu action. */
/** `editor-action` = a Monaco `addAction` entry, reachable from both the
 *  editor context menu and the F1 command palette (Monaco doesn't say which). */
export type NavTrigger = 'keybinding' | 'context-menu' | 'editor-action';

// ─── Discriminated-union event type ─────────────────────────────────────────

export type NavFeatureEvent =
  | { readonly name: 'feature:navfeatures_goto_definition'; readonly props: { readonly via: GotoVia } }
  | { readonly name: 'feature:navfeatures_code_action'; readonly props: { readonly action: CodeActionId } }
  | {
      readonly name: 'feature:navfeatures_find_references';
      readonly props: { readonly kind: IdentifierKind; readonly trigger: NavTrigger };
    }
  | {
      /** Monaco's native cross-object peek (Peek References / Go to
       *  References / Shift+F12). Distinct from `find_references`, which is
       *  the grouped-tab surface. `hasResults` is a boolean, never a count —
       *  keeps the property low-cardinality and free of schema shape. */
      readonly name: 'feature:navfeatures_peek_references';
      readonly props: { readonly kind: IdentifierKind; readonly hasResults: boolean };
    }
  | {
      readonly name: 'feature:navfeatures_rename_invoke';
      readonly props: { readonly kind: RenameKind; readonly source: NavTrigger };
    }
  | { readonly name: 'feature:navfeatures_rename_apply'; readonly props: { readonly kind: RenameKind } }
  | { readonly name: 'feature:navfeatures_rename_undo'; readonly props: { readonly kind: RenameKind } }
  /** F2 on a buffer-local name (alias / CTE) — a real Monaco WorkspaceEdit,
   *  never the preview dialog. Distinct from `rename_invoke`, which counts the
   *  catalog-object path. `occurrences` is a plain count (no identifier text). */
  | {
      readonly name: 'feature:navfeatures_rename_buffer';
      readonly props: { readonly kind: 'alias' | 'cte'; readonly occurrences: number };
    }
  /** A successful DB-object rename rewrote references in N open query tabs. */
  | {
      readonly name: 'feature:navfeatures_rename_tab_rewrite';
      readonly props: { readonly kind: RenameKind; readonly tabs: number };
    };

// ─── Guarded fire-and-forget helper ─────────────────────────────────────────

// No per-run caps. `goto_definition` used to be capped at 50 per app run
// because Monaco's DefinitionProvider also runs on every Ctrl/Cmd+hover link
// probe; since 2026-09-24 the event fires from the Go to Definition editor
// opener (`go-to/definitionNavigationOpener.ts`), once per real navigation, so
// a cap would only under-count heavy users. Runaway loops are still bounded by
// the main-process event-storm guard (`electron/analytics/eventStormGuard.ts`).

/** Fire a navigational-features analytics event.  Never throws, never blocks.
 *  No-ops when running outside a browser window (unit tests without a stub,
 *  Electron main process). */
export function trackNavFeature(event: NavFeatureEvent): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;
  window.electronAPI.analytics.track(event.name, event.props).catch(() => {});
}
